import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';

const Card: React.FC<{ title: string; icon?: string; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div className="p-5 rounded-xl bg-gray-900/70 border border-gray-800">
    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">{icon && <i className={icon}></i>} <span>{title}</span></h3>
    {children}
  </div>
);

const PlotPage: React.FC = () => {
  const { token } = useAuth();
  const [plotId, setPlotId] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [plot, setPlot] = useState<any>(null);
  const [myPlots, setMyPlots] = useState<any[]>([]);

  const wrap = async (fn: () => Promise<void>) => {
    setMessage(null);
    try {
      await fn();
    } catch (err: any) {
      setMessage(err?.message || 'Action failed');
    }
  };

  const { refreshMe } = useAuth();
  const refreshPlot = async () => {
    if (!plotId) return;
    const res = await api.get<{ ok: boolean; plot: any }>(`/plots/${plotId}`, token!);
    setPlot(res.plot);
    setMessage('Loaded plot');
  };

  useEffect(() => {
    if (!token) return;
    api.get<{ ok: boolean; plots: any[] }>(`/plots/mine`, token)
      .then((res) => {
        setMyPlots(res.plots || []);
      })
      .catch(() => setMyPlots([]));
  }, [token]);

  useEffect(() => {
    if (!plotId && myPlots.length > 0) setPlotId(myPlots[0]._id);
  }, [myPlots, plotId]);

  const createPlot = async () => {
    setMessage(null);
    const res = await api.post<{ ok: boolean; plot: any; cost?: number; balance?: number; plotNumber?: number }>(`/plots`, undefined, token!);
    setPlotId(res.plot._id);
    setPlot(res.plot);
    await refreshMe();
    // refresh list to include new plot
    const mine = await api.get<{ ok: boolean; plots: any[] }>(`/plots/mine`, token!);
    setMyPlots(mine.plots || []);
    setMessage(`Plot created${typeof res.cost === 'number' ? `, cost $${res.cost}` : ''}`);
  };

  return (
    <div className="space-y-6">
      {(!plotId && myPlots.length === 0) ? (
        <Card title="Create your first plot" icon="fas fa-map text-emerald-400">
          <p className="text-sm text-gray-400 mb-3">You don’t have any plots yet. Create one to start irrigating, planting, ploughing, and more.</p>
          <button className="px-4 py-2 rounded bg-green-600 hover:bg-green-500" onClick={() => wrap(createPlot)}>Create Plot</button>
        </Card>
      ) : (
        <Card title="Select Plot" icon="fas fa-map-marker-alt text-amber-400">
          <div className="flex gap-2 items-center">
            <select className="flex-1 px-3 py-2 rounded bg-gray-800 text-white" value={plotId} onChange={(e) => setPlotId(e.target.value)}>
              {myPlots.map((p, idx) => (
                <option key={p._id} value={p._id}>Plot {idx + 1} ({String(p._id).slice(-6)})</option>
              ))}
            </select>
            <button className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600" onClick={() => wrap(refreshPlot)}>Load</button>
          </div>
          <div className="mt-3 flex items-center gap-2">
            {myPlots.length < 10 ? (
              <>
                <span className="text-xs text-gray-400">Next plot cost:</span>
                <span className="text-sm text-gray-200 font-semibold">
                  {myPlots.length === 0 ? '$0' : `$${(10000 * Math.pow(2, myPlots.length - 1)).toLocaleString()}`}
                </span>
                <button className="ml-auto px-3 py-1 rounded bg-green-600 hover:bg-green-500" onClick={() => wrap(createPlot)}>Buy New Plot</button>
              </>
            ) : (
              <span className="text-xs text-red-400">Plot limit reached (10)</span>
            )}
          </div>
          {myPlots.length > 0 && (
            <div className="mt-3 text-xs text-gray-400">Your plots: {myPlots.map((p) => p._id).join(', ')}</div>
          )}
          {plot && (
            <div className="mt-3 space-y-1 text-sm text-gray-300">
              <div>Soil pH: {plot?.pH ?? 'N/A'}</div>
              <div>Surface moisture: {(plot?.surfaceMoisture ?? 0).toFixed(2)}</div>
              <div>Root moisture: {(plot?.rootZoneMoisture ?? 0).toFixed(2)}</div>
              <div>Soil quality: {(plot?.soilQuality ?? 0.6).toFixed(2)}</div>
            </div>
          )}
        </Card>
      )}

      {plot && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card title="Irrigate" icon="fas fa-water text-sky-400">
            <IrrigateForm plotId={plotId} token={token!} onDone={() => wrap(refreshPlot)} />
          </Card>
          <Card title="Plant Crop" icon="fas fa-seedling text-green-400">
            <PlantForm plotId={plotId} token={token!} onDone={() => wrap(refreshPlot)} />
          </Card>
          <Card title="Apply Fertilizer" icon="fas fa-shield-alt text-amber-400">
            <FertilizerForm plotId={plotId} token={token!} onDone={() => wrap(refreshPlot)} />
          </Card>
          <Card title="Plough Land" icon="fas fa-tractor text-purple-400">
            <PloughForm plotId={plotId} token={token!} onDone={() => wrap(refreshPlot)} />
          </Card>
          <div className="md:col-span-2">
            <Card title="Plot Spots (10x10 Grid)" icon="fas fa-th text-indigo-400">
              <PlotGrid plotId={plotId} token={token!} plot={plot} onChanged={() => wrap(refreshPlot)} />
            </Card>
          </div>
          <div className="md:col-span-2">
            <Card title="Satellite Report" icon="fas fa-satellite text-indigo-400">
              <SatelliteReport plotId={plotId} token={token!} />
            </Card>
          </div>
        </div>
      )}

      {message && <div className="text-green-400">{message}</div>}
    </div>
  );
};

const IrrigateForm: React.FC<{ plotId: string; token: string; onDone: () => void }> = ({ plotId, token, onDone }) => {
  const [inches, setInches] = useState(0.8);
  const [resMsg, setResMsg] = useState<string | null>(null);
  const submit = async () => {
    setResMsg(null);
    const res = await api.post<{ ok: boolean; surfaceMoisture: number; rootZoneMoisture: number; balance: number }>(`/plots/${plotId}/irrigate`, { inches }, token);
    setResMsg(`Surface: ${res.surfaceMoisture.toFixed(2)} · Root: ${res.rootZoneMoisture.toFixed(2)} · Balance: $${Number(res.balance).toFixed(2)}`);
    onDone();
  };
  return (
    <div className="space-y-3">
      <div className="text-xs text-gray-400">Irrigation adds water to surface moisture. Use modest amounts to avoid nutrient leaching.</div>
      <div className="flex gap-2">
        <input className="flex-1 px-3 py-2 rounded bg-gray-800 text-white" type="number" step="0.1" value={inches} onChange={(e) => setInches(parseFloat(e.target.value))} />
        <button className="px-4 py-2 rounded bg-green-600 hover:bg-green-500" onClick={submit}>Irrigate</button>
      </div>
      {resMsg && <div className="text-sm text-green-300">{resMsg}</div>}
    </div>
  );
};

const PlantForm: React.FC<{ plotId: string; token: string; onDone: () => void }> = ({ plotId, token, onDone }) => {
  const { user, refreshMe } = useAuth();
  const seeds = (user?.inventory || []).filter((i) => i.itemId.startsWith('seed-') && i.qty > 0);
  const [seedLotId, setSeedLotId] = useState(seeds[0]?.itemId || '');
  useEffect(() => {
    if (!seeds.find((s) => s.itemId === seedLotId)) {
      const next = seeds[0]?.itemId || '';
      setSeedLotId(next);
    }
  }, [seeds, seedLotId]);
  const submit = async () => {
    const available = (user?.inventory || []).filter((i) => i.itemId.startsWith('seed-') && i.qty > 0);
    let useId = seedLotId;
    if (!available.find((s) => s.itemId === useId)) {
      // auto-switch to next available
      useId = available[0]?.itemId || '';
      setSeedLotId(useId);
      if (!useId) return; // none available => do not plant
    }
    // Plant in a random available spot
    const resPlot = await api.get<{ ok: boolean; plot: any }>(`/plots/${plotId}`, token);
    const spots: any[] = Array.isArray(resPlot.plot?.spots) && resPlot.plot.spots.length === 100 ? resPlot.plot.spots : Array.from({ length: 100 }, () => ({}));
    const empties = spots.map((s, idx) => (!s?.plantedAt ? idx : -1)).filter((i) => i !== -1);
    if (empties.length === 0) return; // no available spot
    const index = empties[Math.floor(Math.random() * empties.length)];
    await api.post(`/plots/${plotId}/plant-spot`, { index, seedLotId: useId }, token);
    await refreshMe();
    // if selected lot is exhausted, switch for next action
    const after = (user?.inventory || []).filter((i) => i.itemId.startsWith('seed-') && i.qty > 0);
    if (!after.find((s) => s.itemId === useId)) setSeedLotId(after[0]?.itemId || '');
    onDone();
  };
  return (
    <div className="space-y-3">
      <div className="text-xs text-gray-400">Choose from your seed inventory</div>
      <div className="flex gap-2">
        <select className="flex-1 px-3 py-2 rounded bg-gray-800 text-white" value={seedLotId} onChange={(e) => {
          setSeedLotId(e.target.value);
        }}>
          {seeds.map((s) => (
            <option key={s.itemId} value={s.itemId}>{s.name} (x{s.qty})</option>
          ))}
        </select>
        <button className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-500" onClick={submit} disabled={!seedLotId}>Plant</button>
      </div>
    </div>
  );
};

const FertilizerForm: React.FC<{ plotId: string; token: string; onDone: () => void }> = ({ plotId, token, onDone }) => {
  const { user, refreshMe } = useAuth();
  const ferts = (user?.inventory || []).filter((i) => i.itemId.startsWith('fert-') && i.qty > 0);
  const [itemId, setItemId] = useState(ferts[0]?.itemId || '');
  const submit = async () => {
    if (!itemId) return;
    await api.post(`/plots/${plotId}/apply-fertilizer`, { itemId }, token);
    await refreshMe();
    const after = (user?.inventory || []).filter((i) => i.itemId.startsWith('fert-') && i.qty > 0);
    if (!after.find((f) => f.itemId === itemId)) setItemId(after[0]?.itemId || '');
    onDone();
  };
  return (
    <div className="space-y-3">
      <div className="text-xs text-gray-400">Choose from your fertilizers</div>
      <div className="flex gap-2">
        <select className="flex-1 px-3 py-2 rounded bg-gray-800 text-white" value={itemId} onChange={(e) => {
          setItemId(e.target.value);
        }}>
          {ferts.map((f) => (
            <option key={f.itemId} value={f.itemId}>{f.name} (x{f.qty})</option>
          ))}
        </select>
        <button className="px-4 py-2 rounded bg-yellow-600 hover:bg-yellow-500" onClick={submit} disabled={!itemId}>Apply</button>
      </div>
    </div>
  );
};

const PloughForm: React.FC<{ plotId: string; token: string; onDone: () => void }> = ({ plotId, token, onDone }) => {
  const { user, refreshMe } = useAuth();
  const tools = (user?.inventory || []).filter((i) => i.itemId.startsWith('tool-') && i.qty > 0);
  const [toolId, setToolId] = useState(tools[0]?.itemId || '');
  const [msg, setMsg] = useState<string | null>(null);
  const submit = async () => {
    setMsg(null);
    if (!toolId) return;
    const res = await api.post<{ ok: boolean; soilQuality: number }>(`/plots/${plotId}/plough`, { toolId }, token);
    await refreshMe();
    const after = (user?.inventory || []).filter((i) => i.itemId.startsWith('tool-') && i.qty > 0);
    if (!after.find((t) => t.itemId === toolId)) setToolId(after[0]?.itemId || '');
    onDone();
    setMsg(`Soil quality improved to ${res.soilQuality.toFixed(2)}`);
  };
  return (
    <div className="space-y-3">
      <div className="text-xs text-gray-400">Ploughing improves soil tilth and infiltration. Select a tool to use.</div>
      <div className="flex gap-2">
        <select className="flex-1 px-3 py-2 rounded bg-gray-800 text-white" value={toolId} onChange={(e)=>setToolId(e.target.value)}>
          {tools.map((t)=>(<option key={t.itemId} value={t.itemId}>{t.name} (x{t.qty})</option>))}
        </select>
        <button className="px-4 py-2 rounded bg-purple-600 hover:bg-purple-500" onClick={submit} disabled={!toolId}>Plough</button>
      </div>
      {msg && <div className="text-sm text-purple-300">{msg}</div>}
    </div>
  );
};

const SatelliteReport: React.FC<{ plotId: string; token: string }> = ({ plotId, token }) => {
  const [report, setReport] = useState<any | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  const load = async () => {
    const res = await api.get(`/plots/${plotId}/satellite-report`, token);
    setReport(res);
    setShowRaw(false);
  };

  const moisturePct = (v?: number) => Math.round(((v ?? 0) * 100));
  const ndviColor = (ndvi?: number) => {
    const v = ndvi ?? 0;
    if (v >= 0.6) return 'text-emerald-400';
    if (v >= 0.4) return 'text-yellow-300';
    return 'text-red-400';
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <button className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600" onClick={load}>Fetch Report</button>
        {report && (
          <button className="px-3 py-2 rounded bg-gray-800 hover:bg-gray-700 text-xs" onClick={() => setShowRaw((s) => !s)}>
            {showRaw ? 'Hide Raw' : 'Show Raw'}
          </button>
        )}
      </div>

      {report && !showRaw && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Region */}
          <div className="p-4 rounded-lg bg-gray-900/70 border border-gray-800">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <i className="fas fa-globe text-indigo-400"></i>
              Region
            </div>
            <div className="mt-2 text-lg font-semibold">{report.regionKey || 'n/a'}</div>
          </div>

          {/* Rainfall (GPM) */}
          <div className="p-4 rounded-lg bg-gray-900/70 border border-gray-800">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <i className="fas fa-cloud-rain text-sky-400"></i>
              Rain (last 6h)
            </div>
            <div className="mt-2 text-2xl font-semibold">{report?.gpm?.last6h ?? 0}<span className="text-sm ml-1">mm</span></div>
            <div className="text-xs text-gray-400">Intensity: {report?.gpm?.intensity ?? 'n/a'}</div>
          </div>

          {/* Soil Moisture (SMAP) */}
          <div className="p-4 rounded-lg bg-gray-900/70 border border-gray-800">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <i className="fas fa-water text-emerald-400"></i>
              Surface Moisture
            </div>
            <div className="mt-2">
              <div className="h-2 bg-gray-800 rounded">
                <div className="h-2 bg-emerald-500 rounded" style={{ width: `${moisturePct(report?.smap?.surfaceMoisture)}%` }}></div>
              </div>
              <div className="mt-1 text-xs text-gray-400 flex items-center gap-2">
                <span>{moisturePct(report?.smap?.surfaceMoisture)}%</span>
                <span className={`px-2 py-0.5 rounded bg-gray-800 ${report?.smap?.freeze ? 'text-blue-300' : 'text-gray-300'}`}>
                  {report?.smap?.freeze ? 'Freeze risk' : 'No freeze'}
                </span>
                <span>As of {report?.smap?.asOf ?? '—'}</span>
              </div>
            </div>
          </div>

          {/* Power/Meteo */}
          <div className="p-4 rounded-lg bg-gray-900/70 border border-gray-800">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <i className="fas fa-sun text-amber-400"></i>
              Weather
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
              <div className="p-2 rounded bg-gray-800/60"><div className="text-gray-400">Tmin</div><div className="text-white font-semibold">{report?.power?.tMin ?? '—'}°C</div></div>
              <div className="p-2 rounded bg-gray-800/60"><div className="text-gray-400">Tmax</div><div className="text-white font-semibold">{report?.power?.tMax ?? '—'}°C</div></div>
              <div className="p-2 rounded bg-gray-800/60"><div className="text-gray-400">RH</div><div className="text-white font-semibold">{report?.power?.rh ?? '—'}%</div></div>
              <div className="p-2 rounded bg-gray-800/60"><div className="text-gray-400">Wind</div><div className="text-white font-semibold">{report?.power?.wind ?? '—'} m/s</div></div>
              <div className="p-2 rounded bg-gray-800/60 col-span-2"><div className="text-gray-400">Solar</div><div className="text-white font-semibold">{report?.power?.solar ?? '—'} MJ/m²</div></div>
            </div>
          </div>

          {/* MODIS */}
          <div className="p-4 rounded-lg bg-gray-900/70 border border-gray-800">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <i className="fas fa-satellite text-indigo-400"></i>
              MODIS
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
              <div className="p-2 rounded bg-gray-800/60"><div className="text-gray-400">LST Day</div><div className="text-white font-semibold">{report?.modis?.lstDay ?? '—'}°C</div></div>
              <div className="p-2 rounded bg-gray-800/60"><div className="text-gray-400">NDVI</div><div className={`font-semibold ${ndviColor(report?.ndvi ?? report?.modis?.ndvi)}`}>{report?.modis?.ndvi ?? report?.ndvi ?? '—'}</div></div>
            </div>
          </div>

          {/* Advice */}
          <div className="p-4 rounded-lg bg-gray-900/70 border border-gray-800 md:col-span-2 lg:col-span-3">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <i className="fas fa-lightbulb text-yellow-300"></i>
              Advice
            </div>
            <ul className="mt-2 list-disc list-inside text-sm text-gray-200">
              {(report?.advice || []).map((a: string, i: number) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {report && showRaw && (
        <pre className="bg-gray-800 p-3 rounded text-xs overflow-auto">{JSON.stringify(report, null, 2)}</pre>
      )}
    </div>
  );
};

export default PlotPage;

const PlotGrid: React.FC<{ plotId: string; token: string; plot: any; onChanged: () => void }> = ({ plotId, token, plot, onChanged }) => {
  const { user, refreshMe } = useAuth();
  const seeds = (user?.inventory || []).filter((i) => i.itemId.startsWith('seed-') && i.qty > 0);
  const [seedLotId, setSeedLotId] = useState(seeds[0]?.itemId || '');
  const [msg, setMsg] = useState<string | null>(null);
  const [now, setNow] = useState<number>(Date.now());

  // Tick every second so timers update in real time
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!seeds.find((s) => s.itemId === seedLotId)) {
      setSeedLotId(seeds[0]?.itemId || '');
    }
  }, [seeds, seedLotId]);

  const spots: any[] = Array.isArray(plot?.spots) && plot.spots.length === 100 ? plot.spots : Array.from({ length: 100 }, () => ({}));

  const timeLeft = (plantedAt?: string | Date, readyAt?: string | Date) => {
    if (!plantedAt) return 0;
    if (readyAt) {
      const secs = Math.ceil((new Date(readyAt).getTime() - now) / 1000);
      return Math.max(0, secs);
    }
    const secs = 60 - Math.floor((now - new Date(plantedAt).getTime()) / 1000);
    return Math.max(0, secs);
  };

  const plantAt = async (index: number) => {
    setMsg(null);
    let useId = seedLotId;
    const available = seeds;
    if (!useId || !available.find((s) => s.itemId === useId)) {
      useId = available[0]?.itemId || '';
      setSeedLotId(useId);
      if (!useId) { setMsg('No seeds available'); return; }
    }
    await api.post(`/plots/${plotId}/plant-spot`, { index, seedLotId: useId }, token);
    await refreshMe();
    onChanged();
  };

  const harvestAt = async (index: number) => {
    setMsg(null);
    const res = await api.post<{ ok: boolean; yield: number; cropType: string }>(`/plots/${plotId}/harvest-spot`, { index }, token);
    await refreshMe();
    onChanged();
    setMsg(`Harvested ${res.yield} ${res.cropType}`);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center">
        <select className="flex-1 px-3 py-2 rounded bg-gray-800 text-white" value={seedLotId} onChange={(e)=>setSeedLotId(e.target.value)}>
          {seeds.map((s)=>(<option key={s.itemId} value={s.itemId}>{s.name} (x{s.qty})</option>))}
        </select>
        <span className="text-xs text-gray-400">Click an empty cell to plant</span>
      </div>
      <div className="grid grid-cols-10 gap-1">
        {spots.map((spot, idx) => {
          const status = spot?.plantedAt ? (timeLeft(spot.plantedAt, spot.readyAt) > 0 ? 'growing' : 'ready') : 'empty';
          const baseClass = 'h-14 rounded flex flex-col items-center justify-center text-[11px]';
          const cls = status === 'empty' ? `${baseClass} bg-gray-800 border border-gray-700 hover:bg-gray-700 cursor-pointer`
            : status === 'growing' ? `${baseClass} bg-yellow-900/50 border border-yellow-800`
            : `${baseClass} bg-green-900/50 border border-green-800 hover:bg-green-800 cursor-pointer`;
          return (
            <div key={idx} className={cls} onClick={() => {
              if (status === 'empty') plantAt(idx);
              if (status === 'ready') harvestAt(idx);
            }}>
              {status === 'empty' && <span className="text-gray-400">Empty</span>}
              {status === 'growing' && (
                <>
                  <span className="font-semibold text-gray-200">{String(spot.cropType)}</span>
                  <span className="text-gray-300">{timeLeft(spot.plantedAt, spot.readyAt)}s</span>
                </>
              )}
              {status === 'ready' && (
                <>
                  <span className="font-semibold text-emerald-300">{String(spot.cropType)}</span>
                  <span className="text-emerald-200">Ready</span>
                </>
              )}
            </div>
          );
        })}
      </div>
      {msg && <div className="text-sm text-gray-300">{msg}</div>}
    </div>
  );
};